import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../services/api';
import AppHeader from '../../components/layout/AppHeader';
import { FiCheckCircle, FiShield, FiTrendingUp, FiMapPin, FiClock, FiDownload, FiDollarSign, FiShare2, FiZap, FiX } from 'react-icons/fi';
import WorkModeToggle from '../../components/talent/WorkModeToggle';
import SkillDiscoveryWizard from '../../components/talent/SkillDiscoveryWizard';
import PredictorSection from '../../components/talent/PredictorSection';
import { useAuth } from '../../context/AuthContext';

const SkillPassport = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [passport, setPassport] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDiscovery, setShowDiscovery] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const isOwner = user?.id === userId;

    const handleDownloadPDF = async () => {
        if (!userId || !passport) return;
        try {
            setDownloading(true);
            const blob = await apiClient.downloadSkillPassportPdf(userId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `SkillPassport_${passport.name.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('Failed to download PDF:', err);
            alert('Failed to generate PDF. Please try again later.');
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        const fetchPassport = async () => {
            if (!userId) return;
            try {
                setLoading(true);
                const data = await apiClient.getSkillPassport(userId);
                setPassport(data.passport);
            } catch (err: any) {
                console.error('Failed to load passport:', err);
                setError(err.message || 'Failed to load skill passport');
            } finally {
                setLoading(false);
            }
        };

        fetchPassport();
    }, [userId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-premium-wood-dark flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-white/60">Generating Skill Passport...</p>
                </div>
            </div>
        );
    }

    if (error || !passport) {
        return (
            <div className="min-h-screen bg-premium-wood-dark flex items-center justify-center p-4">
                <div className="bg-black/40 backdrop-blur-md border border-red-500/20 p-8 rounded-2xl max-w-md text-center">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-serif text-white mb-2">Passport Not Found</h2>
                    <p className="text-white/60 mb-6">{error || 'The requested skill passport could not be generated.'}</p>
                    <button onClick={() => navigate('/talent')} className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all">
                        Back to Talent Network
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
            <div className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay bg-[url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')] bg-cover" />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/80 via-premium-wood-dark/95 to-black pointer-events-none" />

            <AppHeader />

            <main className="relative z-10 container mx-auto px-4 pt-12 pb-24 max-w-5xl">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row gap-8 items-center md:items-end mb-12">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-40 h-40 rounded-full border-4 border-premium-gold/30 p-1 relative"
                    >
                        <div className="w-full h-full rounded-full bg-black/40 bg-cover bg-center overflow-hidden flex items-center justify-center text-6xl"
                            style={{ backgroundImage: passport.avatar ? `url(${passport.avatar})` : undefined }}>
                            {!passport.avatar && (passport.name?.[0] || 'U')}
                        </div>
                        {passport.verified && (
                            <div className="absolute -bottom-2 -right-2 bg-premium-gold text-premium-wood-dark p-2 rounded-full shadow-lg">
                                <FiShield className="text-xl" />
                            </div>
                        )}
                    </motion.div>

                    <div className="flex-1 text-center md:text-left">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                                <h1 className="text-4xl md:text-5xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light via-premium-gold to-premium-gold-dark">
                                    {passport.name}
                                </h1>
                                {passport.verified && (
                                    <span className="bg-premium-gold/20 text-premium-gold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-premium-gold/30 font-bold">
                                        Verified
                                    </span>
                                )}
                            </div>
                            <p className="text-xl text-white/80 mb-4">{passport.headline}</p>

                            <div className="flex flex-wrap justify-center md:justify-start gap-4 text-white/40 text-sm">
                                <div className="flex items-center gap-1.5"><FiMapPin /> {passport.location}</div>
                                <div className="flex items-center gap-1.5"><FiClock /> Member since {new Date(passport.passportStats.memberSince).toLocaleDateString()}</div>
                                <div className="flex items-center gap-1.5"><FiTrendingUp /> {passport.reliabilityScore}% Reliability</div>
                            </div>
                        </motion.div>
                    </div>

                    <div className="flex gap-3">
                        {isOwner && (
                            <button
                                onClick={() => setShowDiscovery(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-bold transition-all shadow-lg"
                            >
                                <FiZap className="text-premium-gold" /> Discover Skills
                            </button>
                        )}
                        <button className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all" title="Share Passport">
                            <FiShare2 />
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            disabled={downloading}
                            className="flex items-center gap-2 px-6 py-3 bg-premium-gold text-premium-wood-dark border-0 rounded-xl font-bold shadow-lg hover:shadow-premium-gold/20 transition-all disabled:opacity-50"
                        >
                            {downloading ? (
                                <span className="flex items-center gap-2">
                                    <div className="animate-spin w-4 h-4 border-2 border-premium-wood-dark border-t-transparent rounded-full"></div>
                                    Preparing...
                                </span>
                            ) : (
                                <><FiDownload /> Export PDF</>
                            )}
                        </button>
                    </div>
                </div>

                {/* 4 Pillar Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Left Column: Stats & Trust */}
                    <div className="lg:col-span-1 space-y-8">
                        {/* Reliability Card */}
                        <motion.div
                            whileHover={{ translateY: -5 }}
                            className="bg-black/30 backdrop-blur-md border border-white/5 p-6 rounded-2xl"
                        >
                            <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold mb-6">Trust Infrastructure</h3>

                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between items-end mb-2">
                                        <span className="text-sm font-medium">Reliability Score</span>
                                        <span className="text-3xl font-serif text-premium-gold">{passport.reliabilityScore}%</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${passport.reliabilityScore}%` }}
                                            className="h-full bg-premium-gold shadow-[0_0_10px_#d4af37]"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white/5 p-4 rounded-xl text-center">
                                        <div className="text-2xl font-serif text-premium-gold mb-1">{passport.passportStats.totalProjectsCompleted}</div>
                                        <div className="text-[10px] text-white/40 uppercase tracking-wider">Jobs Done</div>
                                    </div>
                                    <div className="bg-white/5 p-4 rounded-xl text-center">
                                        <div className="text-2xl font-serif text-premium-gold mb-1">{passport.rating}</div>
                                        <div className="text-[10px] text-white/40 uppercase tracking-wider">Avg Rating</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-4 bg-green-500/5 border border-green-500/20 rounded-xl text-green-300">
                                    <FiCheckCircle className="shrink-0" />
                                    <p className="text-xs">This professional has a **{passport.passportStats.repeatClientRate}% repeat client rate**, indicating high excellence in service delivery.</p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Certification Card */}
                        <div className="bg-black/30 backdrop-blur-md border border-white/5 p-6 rounded-2xl">
                            <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold mb-4">Skill Foundation</h3>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-premium-gold/20 rounded-lg flex items-center justify-center text-premium-gold text-xl italic font-serif">
                                    {passport.certificationMode === 'formal' ? 'F' : passport.certificationMode[0].toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-bold capitalize">{passport.certificationMode} Professional</div>
                                    <div className="text-[10px] text-white/40">Mode: {passport.certificationMode}</div>
                                </div>
                            </div>

                            {passport.informalCredentials && (
                                <div className="space-y-3 pt-4 border-t border-white/5">
                                    {passport.informalCredentials.learnedFrom && (
                                        <div>
                                            <div className="text-[9px] text-white/40 uppercase">Learned From</div>
                                            <div className="text-sm">{passport.informalCredentials.learnedFrom}</div>
                                        </div>
                                    )}
                                    {passport.informalCredentials.communityReputation && (
                                        <div>
                                            <div className="text-[9px] text-white/40 uppercase">Community Rep</div>
                                            <div className="text-sm italic">"{passport.informalCredentials.communityReputation}"</div>
                                        </div>
                                    )}
                                    {passport.informalCredentials.yearsHandsOnExperience && (
                                        <div className="flex items-center gap-2 text-sm text-premium-gold">
                                            <FiTrendingUp /> {passport.informalCredentials.yearsHandsOnExperience} years hands-on work
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Financial Stability Proof (Sprint 4) */}
                        <motion.div
                            whileHover={{ translateY: -5 }}
                            className="bg-black/30 backdrop-blur-md border border-premium-gold/10 p-6 rounded-2xl"
                        >
                            <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold mb-6">Financial Infrastructure</h3>
                            <div className="space-y-6">
                                <div className="flex justify-between items-center bg-white/5 p-4 rounded-xl border border-white/5">
                                    <div>
                                        <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Total Platform Earnings</div>
                                        <div className="text-2xl font-serif text-premium-gold font-bold">
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: passport.financialProof?.currency || 'USD'
                                            }).format(passport.financialProof?.totalEarnings || 0)}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Status</div>
                                        <div className={`text-xs font-bold ${passport.financialProof?.isFrozen ? 'text-red-500' : 'text-green-500'}`}>
                                            {passport.financialProof?.isFrozen ? 'Frozen' : 'Active'}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-3 bg-white/5 rounded-lg border border-white/5">
                                        <div className="text-[9px] text-white/40 uppercase mb-1">Active Wallets</div>
                                        <div className="text-sm font-bold">{passport.financialProof?.activeWallets || 0} Accounts</div>
                                    </div>
                                    <div className="p-3 bg-white/5 rounded-lg border border-white/5">
                                        <div className="text-[9px] text-white/40 uppercase mb-1">Verification</div>
                                        <div className="text-sm font-bold capitalize">{passport.financialProof?.kycLevel || 'None'}</div>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <div className="flex justify-between items-center text-[10px] text-white/40 uppercase tracking-widest mb-2">
                                        <span>Stability Rating</span>
                                        <span className="text-premium-gold">Verified</span>
                                    </div>
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map(step => (
                                            <div key={step} className={`h-1.5 flex-1 rounded-full ${step <= (passport.financialProof?.kycLevel === 'tier3' ? 5 : passport.financialProof?.kycLevel === 'tier2' ? 4 : 3) ? 'bg-premium-gold shadow-[0_0_5px_#d4af37]' : 'bg-white/10'}`} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Skills & Proof */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* Active Work Modes */}
                        <div className="bg-black/30 backdrop-blur-md border border-white/5 p-8 rounded-2xl">
                            <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold mb-6">Open To Opportunities</h3>
                            <WorkModeToggle initialModes={passport.workModes} readOnly={true} />
                        </div>

                        {/* Verified Skills */}
                        <div className="bg-black/30 backdrop-blur-md border border-white/5 p-8 rounded-2xl">
                            <div className="flex justify-between items-center mb-8">
                                <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold">Verified Skill Ecosystem</h3>
                                <div className="flex items-center gap-1.5 text-xs text-premium-gold">
                                    <FiShield /> {passport.passportStats.verifiedSkillCount} Verified
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {passport.skills.map((skill: any, idx: number) => (
                                    <div key={idx} className="bg-white/5 border border-white/5 p-4 rounded-xl flex items-center justify-between">
                                        <div>
                                            <div className="font-bold flex items-center gap-2">
                                                {skill.name}
                                                {skill.verified && <FiCheckCircle className="text-premium-gold" />}
                                            </div>
                                            <div className="text-[10px] text-white/40">{skill.category}</div>
                                        </div>
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map(star => (
                                                <div key={star} className={`w-1.5 h-3 rounded-full ${star <= skill.proficiency ? 'bg-premium-gold shadow-[0_0_5px_#d4af37]' : 'bg-white/10'}`} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Proof of Work Media */}
                        {passport.proofMedia.length > 0 && (
                            <div className="bg-black/30 backdrop-blur-md border border-white/5 p-8 rounded-2xl">
                                <h3 className="text-white/40 uppercase tracking-widest text-[10px] font-bold mb-8">Project Evidence & Portfolio</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {passport.proofMedia.map((media: any, idx: number) => (
                                        <div key={idx} className="group relative rounded-xl overflow-hidden aspect-video bg-black/40 border border-white/10">
                                            {media.type === 'image' ? (
                                                <img src={media.url} alt={media.caption} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-3xl">🎥</div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all flex flex-col justify-end p-4">
                                                <p className="text-xs font-bold text-white mb-1">{media.caption}</p>
                                                <p className="text-[10px] text-white/60">Uploaded {new Date(media.uploadedAt).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Income Growth Indicator (Pillar 6 Preview) */}
                        <div className="bg-gradient-to-r from-premium-gold/5 via-premium-gold/10 to-transparent border border-premium-gold/10 p-8 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-6">
                            <div>
                                <h4 className="text-premium-gold font-bold mb-1">Empowering Local Economic Growth</h4>
                                <p className="text-white/60 text-sm max-w-md">TechUnity infrastructure helps professionals like {passport.name.split(' ')[0]} increase their global visibility and earning potential.</p>
                            </div>
                            <div className="flex flex-col items-center sm:items-end">
                                <div className="text-3xl font-serif text-premium-gold flex items-center gap-1">
                                    <FiDollarSign className="text-xl" /> {passport.rate}/hr
                                </div>
                                <div className="text-[10px] text-white/40 uppercase tracking-widest">Base Rate Context</div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* AI Opportunity Predictor (Sprint 4) */}
                <div className="mt-12">
                    <PredictorSection />
                </div>
            </main>

            {/* Discovery Modal */}
            <AnimatePresence>
                {showDiscovery && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowDiscovery(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative z-10 w-full max-w-2xl"
                        >
                            <button
                                onClick={() => setShowDiscovery(false)}
                                className="absolute -top-12 right-0 text-white/60 hover:text-white flex items-center gap-2 text-sm uppercase tracking-widest font-bold transition-colors"
                            >
                                <FiX /> Close
                            </button>
                            <SkillDiscoveryWizard
                                onComplete={() => {
                                    setShowDiscovery(false);
                                    // Refresh data if needed, but for now just close
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SkillPassport;
